// src/components/Meta.jsx
import { useEffect, useState, useCallback, useMemo } from 'react';

const Meta = ({ page, profileData = null }) => {
  const [metaData, setMetaData] = useState(null);

  const baseURL = "https://noorunnikah.com";

  // Wrap defaultMeta in useMemo to prevent unnecessary recreations
  const defaultMeta = useMemo(() => ({
    title: "Noor-un-Nikah - Trusted Marriage Bureau & Matchmaking Services",
    description: "Find your perfect match with Noor-un-Nikah - Pakistan's trusted marriage bureau. Professional matchmaking services for Muslims worldwide. Halal matchmaking with verified profiles.",
    keywords: "noorenikah, noor e nikah, noor-e-nikah, noor un nikah, noorunnikah, noor o nikah, nooronikah, noor-o-nikah,  marriage bureau, matchmaking, matrimonial, Muslim marriage, nikah, rishta, marriage proposals, wedding, spouse, matrimony, Pakistan marriage bureau, international marriage bureau",
    imageUrl: "/assets/images/noorun-nikah-og.jpg",
    canonical: "/",
    robots: "index, follow"
  }), []);

  // Page-specific meta templates
  const pageMetaTemplates = useMemo(() => ({
    home: {
      title: "Noor-un-Nikah - Trusted Marriage Bureau & Matchmaking Services",
      description: "Find your perfect match with Noor-un-Nikah - Pakistan's trusted marriage bureau. Professional matchmaking services for Muslims worldwide.",
      keywords: "marriage bureau, matchmaking, Muslim marriage, nikah, rishta",
      imageUrl: "/assets/images/home-og.jpg"
    },
    profiles: {
      title: "Browse Verified Profiles - Noor-un-Nikah Marriage Bureau",
      description: "Browse our database of verified Muslim profiles. Find compatible matches based on religion, education, profession and preferences.",
      keywords: "marriage profiles, Muslim singles, verified profiles, matrimonial profiles"
    },
    profileDetail: {
      title: "{{profileName}} - {{age}} years - {{city}}, {{country}} | Noor-un-Nikah",
      description: "Profile of {{profileName}}, {{age}} years old from {{city}}, {{country}}. {{profession}}. Looking for a life partner.",
      keywords: "profile, marriage profile, match, proposal, matrimony"
    },
    about: {
      title: "About Noor-un-Nikah - Our Mission & Values",
      description: "Learn about Noor-un-Nikah's mission to facilitate halal marriages with trust, privacy and Islamic values.",
      keywords: "about us, mission, values, halal matchmaking"
    },
    contact: {
      title: "Contact Noor-un-Nikah Marriage Bureau",
      description: "Get in touch with our matchmaking consultants. Schedule a consultation or submit your profile.",
      keywords: "contact, inquiry, consultation, register"
    },
    services: {
      title: "Our Matchmaking Services - Noor-un-Nikah",
      description: "Explore our comprehensive matchmaking services including profile creation, verification, and personalized match suggestions.",
      keywords: "services, matchmaking services, marriage consultation"
    },
    privacy: {
      title: "Privacy Policy - Noor-un-Nikah",
      description: "Read our privacy policy to understand how we protect your personal information and ensure confidentiality.",
      keywords: "privacy policy, data protection, confidentiality"
    },
    terms: {
      title: "Terms & Conditions - Noor-un-Nikah",
      description: "Terms and conditions for using Noor-un-Nikah marriage bureau services.",
      keywords: "terms, conditions, agreement"
    },
    faq: {
      title: "Frequently Asked Questions - Noor-un-Nikah",
      description: "Find answers to common questions about our matchmaking process, services, and policies.",
      keywords: "FAQ, questions, help"
    },
    successStories: {
      title: "Success Stories - Noor-un-Nikah Matches",
      description: "Read inspiring stories of successful matches and marriages facilitated by Noor-un-Nikah.",
      keywords: "success stories, testimonials, happy couples"
    },
    membership: {
      title: "Membership Plans - Noor-un-Nikah",
      description: "Choose from our flexible membership plans for access to premium matchmaking services.",
      keywords: "membership, plans, pricing, subscription"
    }
  }), []);

  // Function to replace template variables with actual profile data
  const processMetaData = useCallback((meta, profileInfo) => {
    if (!profileInfo || page !== 'profileDetail') {
      return meta;
    }

    const replacements = {
      '{{profileName}}': profileInfo.full_name || 'Profile',
      '{{age}}': profileInfo.age || '',
      '{{city}}': profileInfo.city || '',
      '{{country}}': profileInfo.country || '',
      '{{profession}}': profileInfo.profession || '',
      '{{religion}}': profileInfo.religion || 'Muslim',
      '{{education}}': profileInfo.education || '',
      '{{image}}': profileInfo.profile_image || '/assets/images/default-profile.jpg'
    };

    const processedMeta = { ...meta };

    // Replace template variables in all string fields
    Object.keys(processedMeta).forEach(key => {
      if (typeof processedMeta[key] === 'string') {
        Object.entries(replacements).forEach(([template, value]) => {
          processedMeta[key] = processedMeta[key].replace(template, value);
        });
      }
    });

    return processedMeta;
  }, [page]);

  const addStructuredData = useCallback((meta, profileInfo) => {
    let schema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Noor-un-Nikah Marriage Bureau",
      "description": meta.description,
      "url": `${baseURL}${meta.canonical || `/${page}`}`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${baseURL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    // Add Organization schema for homepage
    if (page === 'home') {
      schema = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${baseURL}/#organization`,
            "name": "Noor-un-Nikah Marriage Bureau",
            "url": baseURL,
            "logo": {
              "@type": "ImageObject",
              "url": `${baseURL}/assets/images/logo.png`,
              "width": 200,
              "height": 60
            },
            "description": "Professional Muslim matchmaking and marriage bureau services",
            "sameAs": [
              "https://facebook.com/noorunnikah",
              "https://instagram.com/noorunnikah",
              "https://twitter.com/noorunnikah"
            ]
          },
          {
            "@type": "WebSite",
            "@id": `${baseURL}/#website`,
            "url": baseURL,
            "name": "Noor-un-Nikah",
            "description": meta.description,
            "publisher": {
              "@id": `${baseURL}/#organization`
            }
          }
        ]
      };
    }

    // Add Profile schema for profile detail page
    if (page === 'profileDetail' && profileInfo) {
      schema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": profileInfo.full_name,
        "description": meta.description,
        "url": `${baseURL}/profiles/${profileInfo.id || 'profile'}`,
        "image": profileInfo.profile_image ? `${baseURL}${profileInfo.profile_image}` : `${baseURL}/assets/images/default-profile.jpg`,
        "gender": profileInfo.gender === 'Male' ? 'Male' : 'Female',
        "nationality": {
          "@type": "Country",
          "name": profileInfo.country || "Pakistan"
        },
        "birthDate": profileInfo.date_of_birth || "",
        "height": profileInfo.height || "",
        "jobTitle": profileInfo.profession || "",
        "knowsAbout": [
          profileInfo.religion || "Islam",
          profileInfo.education || ""
        ].filter(Boolean)
      };
    }

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }, [page, baseURL]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        // First check if we have page-specific template
        const pageTemplate = pageMetaTemplates[page] || pageMetaTemplates.home;

        // Merge with default meta
        const pageMeta = {
          ...defaultMeta,
          ...pageTemplate,
          canonical: page === 'home' ? '/' : `/${page}`
        };

        const processedMeta = processMetaData(pageMeta, profileData);
        setMetaData(processedMeta);

      } catch (error) {
        console.error('Error processing metadata:', error);
        const processedMeta = processMetaData(defaultMeta, profileData);
        setMetaData(processedMeta);
      }
    };

    fetchMetadata();
  }, [page, defaultMeta, profileData, processMetaData, pageMetaTemplates]);

  useEffect(() => {
    if (!metaData) return;

    // Set document title
    document.title = metaData.title;

    // Define all meta tags
    const metaTags = [
      // Basic meta tags
      { name: 'description', content: metaData.description },
      { name: 'keywords', content: metaData.keywords },
      { name: 'robots', content: metaData.robots || 'index, follow' },
      { name: 'googlebot', content: metaData.robots || 'index, follow' },
      { name: 'language', content: 'English' },
      { name: 'author', content: 'Noor-un-Nikah Marriage Bureau' },

      // Open Graph
      { property: 'og:title', content: metaData.title },
      { property: 'og:description', content: metaData.description },
      { property: 'og:type', content: page === 'profileDetail' ? 'profile' : 'website' },
      { property: 'og:image', content: `${baseURL}${metaData.imageUrl}` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:url', content: `${baseURL}${metaData.canonical}` },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:site_name', content: 'Noor-un-Nikah Marriage Bureau' },

      // Twitter
      { name: 'twitter:title', content: metaData.title },
      { name: 'twitter:description', content: metaData.description },
      { name: 'twitter:image', content: `${baseURL}${metaData.imageUrl}` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@noorunnikah' },
      { name: 'twitter:creator', content: '@noorunnikah' }
    ];

    // Additional meta tags for profile detail page
    if (page === 'profileDetail' && profileData) {
      metaTags.push(
        { property: 'og:profile:first_name', content: profileData.first_name || '' },
        { property: 'og:profile:last_name', content: profileData.last_name || '' },
        { property: 'og:profile:gender', content: profileData.gender || '' },
        { property: 'og:profile:username', content: profileData.username || '' }
      );
    }

    // Create or update meta tags
    metaTags.forEach(({ name, property, content }) => {
      if (!content) return;

      const attrName = property ? 'property' : 'name';
      const attrValue = property || name;

      let tag = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Handle canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `${baseURL}${metaData.canonical}`;

    // Add viewport meta tag if not exists
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1';
      document.head.appendChild(viewport);
    }

    // Add structured data
    addStructuredData(metaData, profileData);

    // Cleanup function
    return () => {
      // Remove meta tags (except viewport)
      metaTags.forEach(({ name, property }) => {
        const attrName = property ? 'property' : 'name';
        const attrValue = property || name;
        const tag = document.querySelector(`meta[${attrName}="${attrValue}"]`);
        if (tag && attrName !== 'viewport') tag.remove();
      });

      // Remove canonical link
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();

      // Remove structured data script
      const script = document.querySelector('script[type="application/ld+json"]');
      if (script) script.remove();
    };
  }, [metaData, page, profileData, addStructuredData, baseURL]);

  return null;
};

export default Meta;